import React, { useState, useRef, useEffect } from 'react'
import { Editor, Realtime } from '@minutes/components'
import { useEditor } from 'components/contexts/editor'
import { ClassNames } from '@emotion/react'
import i18n from 'i18next'
import { useSavingContext } from 'components/contexts/saving'
import { Editor as EditorType } from 'components/contexts/editor'
import { ToolbarOptions } from '../../../assets/ckeditor/toolbars'
import selectors from '../../../selectors/minuteTakerSelectors'
import { pathOr } from 'rambdax'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { DOCUMENT_STATUS } from './InvitationView/types'
import { transientStorageManager } from 'businesslayer/minutesSessionStore'

type Props = {
    onChange?: (editor: EditorType) => void
    data?: string
    editorConfig?: Record<string, any>
    onEditorInit?: (editor: any) => void
    realtime?: boolean
    readonly?: boolean
    handleError?: (error: any) => void
    currentMinuteItem: any
    getAiSupport?: (payload: string | Record<string, unknown>) => Promise<unknown>
}

function MinutesEditor({
    onChange,
    data,
    editorConfig,
    onEditorInit,
    realtime,
    readonly,
    handleError,
    currentMinuteItem,
    getAiSupport
}: Props) {
    const editor = useEditor()
    const { setIsSaving } = useSavingContext()
    const [initData] = useState(data)
    const editorRef = useRef<any>(null)
    const [hasFocus, setHasFocus] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    /**
     * Set saving context if CK Editor is pending a save
     * Note: this is only applicable to review editor since
     * thats the only editor using autosave
     */

    useEffect(() => {
        if (
            hasFocus &&
            !!editor.currentEditor &&
            editor.currentEditor.plugins.get('PendingActions')
        ) {
            const pendingActions = editor.currentEditor.plugins.get('PendingActions')
            pendingActions.on('change:hasAny', (_, __, newValue) => {
                if (newValue) {
                    setIsSaving(true)
                } else {
                    setIsSaving(false)
                }
            })
        }
    }, [editor, hasFocus, setIsSaving])

    useEffect(() => {
        if (editorRef.current) {
            if (readonly) {
                editorRef.current.enableReadOnlyMode('globalEditorLock')
            } else {
                editorRef.current.disableReadOnlyMode('globalEditorLock')
            }
        }
    }, [readonly])

    function handleFocus(editorInstance) {
        editor.setCurrentEditor(editorInstance)
        setHasFocus(true)
    }

    function handleBlur() {
        // editor.setCurrentEditor(undefined)
        setHasFocus(false)
    }

    function handleChange() {
        if (onChange && editor) {
            // Disable save on init by checking if data and editor data are different
            const currentEditor = editor.currentEditor
            !!currentEditor && onChange(currentEditor)
        }
    }

    function mountDecoupledUi(editor: any) {
        const host = ref.current
        if (!host) {
            return false
        }
        // CKEditor may restart after a fatal error; clear stale toolbar/editable nodes first.
        host.replaceChildren()
        host.appendChild(editor.ui.view.toolbar.element)
        host.appendChild(editor.ui.getEditableElement())
        return true
    }

    function handleOnInit(editor: any) {
        editorRef.current = editor
        if (!mountDecoupledUi(editor)) {
            requestAnimationFrame(() => {
                if (editorRef.current === editor && mountDecoupledUi(editor)) {
                    !!onEditorInit && onEditorInit(editor)
                }
            })
        } else {
            !!onEditorInit && onEditorInit(editor)
        }
        readonly
            ? editor.enableReadOnlyMode('globalEditorLock')
            : editor.disableReadOnlyMode('globalEditorLock')
    }

    /*function handleToolbarClick() {
        if (editorRef && editorRef.current != null) {
            editorRef!.current!.editing!.view.focus()
        }
    }*/
    /**
     * Enable the list option to active the number,roman and alphabet option for list item.
     */
    const bookSummarizationEnabled = transientStorageManager.book_summarization_enabled
    const AiTranslations = {
        AI_ERROR_CONTENT: i18n.t('AI_ERROR_CONTENT'),
        AI_GEN_RESULT: i18n.t('AI_GEN_RESULT'),
        AI_INSERT: i18n.t('AI_INSERT'),
        AI_HEADER_TITLE: i18n.t('AI_HEADER_TITLE'),
        AI_TOOLBAR_TRANSFORM_BUTTON: i18n.t('AI_TOOLBAR_TRANSFORM_BUTTON'),
        AI_TEXT_EDIT_INPUT_PLACEHOLDER: i18n.t('AI_TEXT_EDIT_INPUT_PLACEHOLDER'),
        AI_ACTION_IMPROVE_WRITING: i18n.t('AI_ACTION_IMPROVE_WRITING'),
        AI_ACTION_MAKE_SHORTER: i18n.t('AI_ACTION_MAKE_SHORTER'),
        AI_ACTION_CHANGE_TENSE: i18n.t('AI_ACTION_CHANGE_TENSE'),
        AI_ACTION_PII: i18n.t('AI_ACTION_PII'),
        AI_TRANSFORM_SELECT_PRESET: i18n.t('AI_TRANSFORM_SELECT_PRESET'),
        AI_REFINE_NEED_INSTRUCTION: i18n.t('AI_REFINE_NEED_INSTRUCTION'),
        AI_INSERT_TRANSFORM_LIMIT_REACHED: i18n.t('AI_INSERT_TRANSFORM_LIMIT_REACHED'),
        AI_INSTRUCTION_MAX_LENGTH: i18n.t('AI_INSTRUCTION_MAX_LENGTH'),
        AI_INSTRUCTION_DISALLOWED_CODE: i18n.t('AI_INSTRUCTION_DISALLOWED_CODE'),
        AI_ENTRY_SELECTION_TOO_SHORT_MEANINGFUL: i18n.t('AI_ENTRY_SELECTION_TOO_SHORT_MEANINGFUL'),
        NO_HTML_MESSAGE: i18n.t('NO_HTML_MESSAGE'),
        AI_SUBMENU_BACK: i18n.t('AI_SUBMENU_BACK'),
        AI_SUBMENU_REGION: i18n.t('AI_SUBMENU_REGION'),
        AI_TENSE_PAST: i18n.t('AI_TENSE_PAST'),
        AI_TENSE_PRESENT: i18n.t('AI_TENSE_PRESENT'),
        AI_ENTRY_CHANGE_TO_PAST_TENSE: i18n.t('AI_ENTRY_CHANGE_TO_PAST_TENSE'),
        AI_ENTRY_CHANGE_TO_PRESENT_TENSE: i18n.t('AI_ENTRY_CHANGE_TO_PRESENT_TENSE'),
        AI_INACCURACIES: i18n.t('AI_INACCURACIES'),
        AI_ERROR_MODAL_HEADER: i18n.t('AI-ERROR-MODAL-HEADER'),
        AI_INSERT_TOAST_MESSAGE: i18n.t('AI_INSERT_TOAST_MESSAGE'),
        AI_NO_ACCESS_ERROR_MESSAGE: i18n.t('AI_NO_ACCESS_ERROR_MESSAGE'),
        COPY: i18n.t('COPY'),
        AI_INSERT_MODAL_COPY_COPIED: i18n.t('AI_INSERT_MODAL_COPY_COPIED'),
        CLOSE: i18n.t('CLOSE'),
        TOAST_DISMISS_BUTTON: i18n.t('TOAST_DISMISS_BUTTON'),
        AI_INSUFFICIENT_TEXT_ERROR: i18n.t('AI_INSUFFICIENT_TEXT_ERROR'),
        AI_COPY_CLIP: i18n.t('AI_COPY_CLIP'),
        DRAG: i18n.t('DRAG'),
        AI_GENERATED_HEADING: i18n.t('AI_GENERATED_HEADING'),
        MINUTES_SHOW_MORE_LABEL: i18n.t('MINUTES_SHOW_MORE_LABEL'),
        AI_TITLE_SHOW_LESS: i18n.t('AI_TITLE_SHOW_LESS'),
        LEARN_MORE: i18n.t('MINUTES_TEMPLATES_LEARN_MORE'),
        USER_LANGUAGE: i18n.language
    }
    const documentStatus = pathOr(
        '',
        ['attributes', 'status'],
        currentMinuteItem
    ) as DocumentStatusKey
    const config = {
        heading: ToolbarOptions.heading,
        highlight: ToolbarOptions.highlight,
        //list: ToolbarOptions.list,
        typing: ToolbarOptions.typing,
        bookSummarizationEnabled,
        AiTranslations,
        ...editorConfig,
        isAISupported: documentStatus === DOCUMENT_STATUS.DRAFT && bookSummarizationEnabled !== null
    }

    return (
        <ClassNames>
            {({ css }) => (
                <>
                    <div
                        className={css`
                            .ck.ck-toolbar {
                                z-index: 95;
                                border-left: 0;
                                border-right: 0;
                                top: ${documentStatus === DOCUMENT_STATUS.DRAFT ? '80px' : '0'};
                                width: 100%;
                            }
                        `}
                        // onClick={handleToolbarClick}
                        ref={ref}
                    />

                    <div
                        className={css`
                            padding: 0 40px;
                            .ck-focused {
                                border: 1px solid transparent !important;
                                box-shadow: none !important;
                            }

                            /* Ensure that long unbroken stings will wrap it all browsers */
                            > .ck-content {
                                overflow-wrap: break-word;
                            }
                        `}>
                        <ErrorBoundary>
                            {realtime ? (
                                <Realtime
                                    placeholder={i18n.t('EDITOR_PLACEHOLDER_TEXT')}
                                    onReady={handleOnInit}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    // onChange={handleChange}
                                    data={initData}
                                    runtimeConfig={config}
                                    handleError={handleError}
                                    getAiSupport={
                                        documentStatus === DOCUMENT_STATUS.DRAFT &&
                                        bookSummarizationEnabled !== null
                                            ? getAiSupport
                                            : undefined
                                    }
                                />
                            ) : (
                                <Editor
                                    placeholder={i18n.t('EDITOR_PLACEHOLDER_TEXT')}
                                    onReady={handleOnInit}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    data={initData}
                                    runtimeConfig={config}
                                    getAiSupport={
                                        documentStatus === DOCUMENT_STATUS.DRAFT &&
                                        bookSummarizationEnabled !== null
                                            ? getAiSupport
                                            : undefined
                                    }
                                />
                            )}
                        </ErrorBoundary>
                    </div>
                </>
            )}
        </ClassNames>
    )
}

class ErrorBoundary extends React.Component {
    state = { hasError: false }

    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI.
        return { hasError: true }
    }

    componentDidCatch(error, info) {
        // You can also log the error to an error reporting service
        console.error(error, info)
    }

    render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return <h1>Something went wrong.</h1>
        }

        return this.props.children
    }
}

const mapStateToProps = (state, _) => {
    return {
        currentMinuteItem: selectors.currentMinuteItem(state.minuteTakerReducer)
    }
}

export default withRouter(connect(mapStateToProps)(MinutesEditor))
